import * as THREE from 'three'
import gsap from 'gsap'
import { loadCoverTexture } from '../utils/Textures'
import { loadRecordModel } from '../utils/RecordModel'
import VinylMaterial from '../materials/VinylMaterial'
import SleeveMaterial from '../materials/SleeveMaterial'
import WebGLContext from '../core/WebGLContext'

/**
 * A sleeve + vinyl disc. All units are CSS pixels (see RecordScene camera).
 *
 * sequence() is this record's page transition; RecordScene adds every record's
 * sequence to one timeline and plays/reverses it. isActive decides whether the
 * record isolates (tuck -> flip -> travel) or slides off screen.
 */
export default class Record extends THREE.Group {
	constructor({ slug, url, cover, label, colour, spots, seed, pattern, accent, core }) {
		super()
		this.slug = slug
		this.url = url
		this.discPeek = window.matchMedia('(max-width: 767px)').matches ? 0.25 : 0.45 // less on phones: the slides are 70vw
		this.discSpin = 0.15
		this.sleeveTilt = 0.05
		this.hoverLift = 0 // scale up under the pointer (off: the tilt is the hover)
		this.hoverPeek = 0.05 // disc slides out this much further under the pointer
		this.pressDip = 0.01 // scale down on pointer down
		this.hoverTilt = 0.2 // radians the record turns to face the pointer at the sleeve's edge

		this.sleeve = Record.#sleeve(cover)
		this.disc = new THREE.Group() // scaled to the pose size each frame; the model sits inside
		Record.#disc({ colour, spots, seed, pattern, accent, core }, label).then(model => this.disc.add(model))
		this.disc.position.z = -4 // behind the sleeve, with room for the record's thickness (~1% of its size)
		this.body = new THREE.Group() // sleeve + disc together; this is what tilts towards the pointer
		this.body.add(this.disc, this.sleeve)
		this.add(this.body)

		// animated values, all 0 -> 1
		this.tuck = 0 // disc slides back into the sleeve
		this.flip = 0 // record spins a full turn; the sleeve is left behind once it faces away
		this.travel = 0 // disc moves from the slider to the page anchor
		this.exit = 0 // non-active record slides off its side of the screen
		this.swap = 0 // isolated record turns edge-on (±0.5) to hand over to another record
		this.hover = 0 // pointer over it: lifts a touch, disc slides out further, turns to face the pointer
		this.look = { x: 0, y: 0 } // where over the sleeve the pointer is, -1..1 each way (y up)
		this.press = 0 // pointer down: pushed in
		this.intro = 1 // first load: rises in from a touch smaller and lower (RecordScene.intro)
		this.introDrop = 0.2 // of the record's size
		this.introScale = 0.92
		// one reusable tween per pointer value - retargeted on every pointermove, see follow()
		const ease = { duration: 0.75, ease: 'power3' }
		this.#follow = [
			gsap.quickTo(this, 'hover', ease),
			gsap.quickTo(this.look, 'x', ease),
			gsap.quickTo(this.look, 'y', ease),
		]
		this.isActive = false // which of the above update() applies

		// last known poses - kept when a page swap removes the slider or the anchor
		this.sliderPose = null
		this.anchorPose = null
	}

	#follow

	/** Pointer over (`on`) the sleeve at `look` (-1..1 each way, y up), or off it. */
	follow(on, { x, y } = { x: 0, y: 0 }) {
		const [hover, lookX, lookY] = this.#follow
		hover(on ? 1 : 0)
		lookX(x)
		lookY(y)
	}

	/** This record's page transition. Each line: what, how long, when it starts (seconds). */
	sequence() {
		return gsap
			.timeline()
			.to(this, { exit: 1, duration: 1.25, ease: 'back.inOut(1)' }, 0.1) // non-active: slide off screen
			.to(this, { tuck: 1, duration: 0.5, ease: 'back.inOut(1)' }, 0) // active: disc into the sleeve
			.to(this, { flip: 1, duration: 1.5, ease: 'back.inOut(1)' }, 0.3) // active: full 360 spin...
			.to(this, { travel: 1, duration: 0.8, ease: 'back.inOut(1)' }, 0.7) // ...while moving + scaling to the anchor
	}

	/**
	 * @param {number} delta   seconds since last frame
	 * @param {Pose|null} slider  this record's pose in the DOM slider (null when no slider on the page)
	 * @param {Pose|null} anchor  the isolated pose from the page's [data-record-anchor]
	 * @param {number} exitX   half the viewport width; records leave sideways past ±exitX
	 * @typedef {{ x: number, y: number, size: number, rotation?: number, peek?: number }} Pose
	 */
	update(delta, slider, anchor, exitX) {
		if (slider) this.sliderPose = slider
		if (anchor) this.anchorPose = anchor
		const from = this.sliderPose ?? this.anchorPose
		const to = this.anchorPose ?? this.sliderPose
		if (!from) return

		this.disc.rotation.z -= delta * this.discSpin

		if (this.isActive) this.#isolateSequence(from, to)
		else this.#slideOut(from, exitX)

		this.scale.setScalar(THREE.MathUtils.lerp(this.introScale, 1, this.intro))
		this.position.y -= (1 - this.intro) * from.size * this.introDrop
	}

	/** Active record: values come straight from the `sequence` timeline. */
	#isolateSequence(from, to) {
		const { lerp } = THREE.MathUtils
		const { tuck, flip, travel, swap } = this
		const size = lerp(from.size, to.size, travel) * this.#touch(1 - travel) // hover/press fade out as it travels

		this.position.set(lerp(from.x, to.x, travel), lerp(from.y, to.y, travel), 0)
		this.rotation.set(0, Math.PI * 2 * flip + Math.PI * swap, lerp(from.rotation ?? 0, 0, travel))
		this.#tilt(1) // follows the pointer in the slider and, once parked, over the anchor

		// the sleeve is left behind at the half-turn, where it's edge-on anyway
		this.sleeve.scale.setScalar(size)
		this.sleeve.visible = flip < 0.5

		this.disc.scale.setScalar(size)
		this.disc.position.x = this.#peek(size, from.peek) * (1 - tuck) * (1 - travel)
	}

	/** Non-active record: slides off its own side of the screen with a lean into the move. */
	#slideOut(from, exitX) {
		const { lerp } = THREE.MathUtils
		const side = Math.sign(from.x) || 1

		this.position.set(lerp(from.x, side * (exitX + from.size), this.exit), from.y, 0)
		this.rotation.z = (from.rotation ?? 0) - side * this.sleeveTilt * this.exit

		const size = from.size * this.#touch(1)
		this.rotation.y = 0
		this.#tilt(1)
		this.sleeve.scale.setScalar(size)
		this.sleeve.visible = true

		this.disc.scale.setScalar(size)
		this.disc.position.x = this.#peek(size, from.peek)
	}

	/** Scale multiplier for hover (up) and press (down), weighted by `amount`. */
	#touch(amount) {
		return 1 + (this.hoverLift * this.hover - this.pressDip * this.press) * amount
	}

	/** Turn the body's face towards the pointer, weighted by `amount`. */
	#tilt(amount) {
		const angle = this.hoverTilt * this.hover * amount
		this.body.rotation.set(-this.look.y * angle, this.look.x * angle, 0)
	}

	/** How far the disc sits out of the sleeve: the slider's peek, plus a bit more on hover. */
	#peek(size, peek = 0) {
		return this.discPeek * size * peek * (1 + this.hoverPeek * this.hover)
	}

	/** The cover under a gloss laminate that catches the key light (see materials/SleeveMaterial.js). */
	static #sleeve(textureUrl) {
		return new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new SleeveMaterial(loadCoverTexture(textureUrl)))
	}

	/**
	 * The record model wearing this record's vinyl (see shaders/vinyl.frag) and label artwork.
	 * Geometry and maps are shared between records; each mesh gets its own material.
	 */
	static async #disc(colours, labelUrl) {
		const { record, label, maps, radius } = await loadRecordModel()
		const size = 0.95 // disc diameter as a fraction of the sleeve
		const context = new WebGLContext()

		const dress = (mesh, extra) => {
			const clone = mesh.clone() // shares the geometry, keeps the mesh's own transform
			clone.material = new VinylMaterial({
				maps,
				scale: mesh.scale.x / (2 * radius),
				...colours,
				environmentMap: context.environment,
				...extra,
			})
			return clone
		}
		const model = new THREE.Group()
		model.scale.setScalar(size / (2 * radius))
		model.add(dress(record), dress(label, labelUrl ? { labelMap: loadCoverTexture(labelUrl) } : {}))

		Object.assign(model.children[1].material, { polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
		return model
	}
}
